import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Clock, XCircle, Loader2, Calendar, Home } from "lucide-react";
import { API } from "../App";
import { fireGoogleAdsConversion } from "../utils/googleAds";
import { trackBookingPurchase } from "../utils/analytics";
import { fireMetaPixelPurchase } from "../utils/metaPixel";

/**
 * Landing page after a successful Revolut payment redirect.
 * URL: /booking-success?booking_id=XXX
 *
 * Flow:
 *   1. Poll GET /api/payments/status/{booking_id} until it returns "paid"
 *      (max ~30s — webhook may race the redirect).
 *   2. Once confirmed, fire the Google Ads conversion (guarded, single-fire).
 *   3. Show a friendly confirmation state.
 */
const POLL_INTERVAL_MS = 2500;
const MAX_POLL_TIME_MS = 30_000;

const BookingSuccessPage = () => {
  const [params] = useSearchParams();
  const bookingId = params.get("booking_id") || "";
  const [status, setStatus] = useState("checking"); // checking | paid | pending | failed | error
  const [amount, setAmount] = useState(50);
  const firedRef = useRef(false);
  const startedRef = useRef(Date.now());

  useEffect(() => {
    if (!bookingId) {
      setStatus("error");
      return;
    }

    let cancelled = false;
    let timeoutId;

    const poll = async () => {
      if (cancelled) return;
      try {
        const r = await axios.get(`${API}/payments/status/${bookingId}`);
        const data = r.data || {};
        if (cancelled) return;

        if (data.status === "paid") {
          setAmount(data.amount_eur || 50);
          setStatus("paid");

          if (!firedRef.current) {
            firedRef.current = true;
            // All THREE conversion signals fire from the SAME server-
            // confirmed "paid" state — Ads for bidding optimisation,
            // GA4 for the ecommerce funnel report, Meta Pixel for
            // Facebook / Instagram ad attribution. Each has its own
            // sessionStorage guard against double-fire on refresh
            // (see analytics.js / googleAds.js / metaPixel.js). Meta
            // silently no-ops when the visitor rejected cookies (base
            // pixel stays in `consent:revoke` state) so it's safe to
            // call unconditionally alongside the other two.
            fireGoogleAdsConversion({
              transactionId: bookingId,
              value: data.amount_eur || 50,
              currency: "EUR",
            });
            trackBookingPurchase({
              transaction_id: bookingId,
              value: data.amount_eur || 50,
              currency: "EUR",
            });
            fireMetaPixelPurchase({
              transaction_id: bookingId,
              value: data.amount_eur || 50,
              currency: "EUR",
            });
          }
          return;
        }

        if (data.status === "failed") {
          setStatus("failed");
          return;
        }

        // Keep polling while we're within the window
        if (Date.now() - startedRef.current < MAX_POLL_TIME_MS) {
          timeoutId = setTimeout(poll, POLL_INTERVAL_MS);
        } else {
          setStatus("pending"); // give up — but customer's booking is safe; webhook will still fire
        }
      } catch (err) {
        console.error("[booking-success] poll error", err);
        if (Date.now() - startedRef.current < MAX_POLL_TIME_MS) {
          timeoutId = setTimeout(poll, POLL_INTERVAL_MS);
        } else {
          setStatus("error");
        }
      }
    };

    poll();

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [bookingId]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-4">
      <div
        className="max-w-lg w-full bg-slate-900/80 border border-slate-700 rounded-2xl shadow-2xl p-8 text-center"
        data-testid="booking-success-page"
      >
        {status === "checking" && (
          <>
            <Loader2 className="w-16 h-16 text-saturn-orange mx-auto mb-4 animate-spin" />
            <h1 className="text-2xl font-bold text-white mb-2">Confirming your payment…</h1>
            <p className="text-slate-400">Just a moment while we finalise your booking.</p>
          </>
        )}

        {status === "paid" && (
          <>
            <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-12 h-12 text-green-400" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2" data-testid="success-heading">
              Booking Confirmed
            </h1>
            <p className="text-slate-300 mb-1">Thanks — your €{amount} deposit has been received.</p>
            <p className="text-slate-500 text-sm mb-6">
              Booking ID:{" "}
              <span className="font-mono text-slate-300">{bookingId}</span>
            </p>
            <div className="bg-slate-800/60 border border-slate-700 rounded-lg p-4 mb-6 text-left">
              <p className="text-white font-medium mb-1 flex items-center gap-2">
                <Calendar className="w-4 h-4 text-saturn-orange" />
                What happens next
              </p>
              <ul className="text-slate-400 text-sm space-y-1 list-disc list-inside">
                <li>You'll receive a confirmation email in the next few minutes.</li>
                <li>We'll see you at your scheduled appointment.</li>
                <li>Aftercare guidance will be sent when your car is ready to collect.</li>
              </ul>
            </div>
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-saturn-orange hover:bg-saturn-orange/80 text-white px-6 py-3 rounded-lg font-medium transition"
              data-testid="back-home-btn"
            >
              <Home className="w-4 h-4" />
              Back to home
            </Link>
          </>
        )}

        {status === "pending" && (
          <>
            <Clock className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2">Payment is processing</h1>
            <p className="text-slate-400 mb-4">
              We haven't received confirmation from the bank yet. This can take a minute — you'll get a confirmation
              email as soon as it clears. No need to pay again.
            </p>
            <p className="text-slate-500 text-sm">
              Booking ID: <span className="font-mono">{bookingId}</span>
            </p>
          </>
        )}

        {status === "failed" && (
          <>
            <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2">Payment was not completed</h1>
            <p className="text-slate-400 mb-6">Your booking is on hold. Please try again or reach out via WhatsApp.</p>
            <Link
              to="/book"
              className="inline-flex items-center gap-2 bg-saturn-orange hover:bg-saturn-orange/80 text-white px-6 py-3 rounded-lg font-medium transition"
            >
              Try again
            </Link>
          </>
        )}

        {status === "error" && (
          <>
            <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2">We couldn't find that booking</h1>
            <p className="text-slate-400 mb-6">If you were charged, contact us via WhatsApp with your name and we'll sort it.</p>
            <Link to="/" className="text-saturn-orange underline">
              Go to homepage
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export default BookingSuccessPage;
